import React from 'react'
import Dropzone from 'react-dropzone'
import { AddPhotoIcon } from '@fourthwall/icons'
import { UploadAreaUI } from '../../ui/UploadArea'
import type { UploadProps } from './types'
import { useMediaQueryContext } from '../../providers/MediaQueryProvider'

export const Upload = ({
  title,
  accept,
  maxSize = 104857600, // 100 MB
  multiple = false,
  isUploading,
  disabled = false,
  invalid = false,
  contentAccepted,
  contentRejected,
  content,
  orientation = 'vertical',
  size = 'large',
  icon = AddPhotoIcon,
  iconSize = 32,
  onFileDrop,
  onFileDialogCancel,
  onFileDropRejected,
  openFileDialogProgrammatically = false,
}: UploadProps) => {
  const media = useMediaQueryContext()
  const defaultTitle = media.tablet ? 'Drop file or browse to upload' : 'Browse to upload'

  const renderDefaultContent = () => {
    if (isUploading) {
      return <UploadAreaUI.Loader size={size} orientation={orientation} />
    }

    return (
      <UploadAreaUI.Hero size={size} orientation={orientation}>
        <UploadAreaUI.HeroIcon icon={icon} iconSize={iconSize} orientation={orientation} />
        {title ?? defaultTitle}
      </UploadAreaUI.Hero>
    )
  }

  /**
   *
   * @param accepted
   * @param rejected
   * @param active
   * @param open - opening file dialog, works only when openFileDialogProgrammatically is true
   */
  const renderContent = (
    accepted: boolean,
    rejected: boolean,
    active: boolean,
    open: () => void,
  ) => {
    if (content) {
      return content({
        accepted,
        rejected,
        active,
        ...(openFileDialogProgrammatically && { open }),
      })
    }

    return (
      <UploadAreaUI.ContentWrapper
        accepted={accepted || !!contentAccepted}
        rejected={rejected || !!contentRejected}
        invalid={invalid}
        disabled={disabled}
        size={size}
      >
        {renderDefaultContent()}
      </UploadAreaUI.ContentWrapper>
    )
  }

  return (
    <Dropzone
      accept={accept}
      maxSize={maxSize}
      disabled={isUploading || disabled}
      multiple={multiple}
      onFileDialogCancel={onFileDialogCancel}
      onDropRejected={onFileDropRejected}
      onDropAccepted={onFileDrop}
      noClick={openFileDialogProgrammatically}
      noKeyboard={openFileDialogProgrammatically}
    >
      {({ getRootProps, getInputProps, isDragAccept, isDragReject, isDragActive, open }) => (
        <UploadAreaUI.Root {...getRootProps()} data-testid="Upload.ContentContainer">
          <input {...getInputProps()} />
          {renderContent(isDragAccept, isDragReject, isDragActive, open)}
        </UploadAreaUI.Root>
      )}
    </Dropzone>
  )
}
